/**
 * Basemap definitions shared by every map in the app.
 *
 * Adding a provider (for example Dataforsyningen for the Danish customers)
 * means adding an entry to the registry in BasemapService — no component
 * touches tile URLs directly.
 */

export enum BasemapId {
  Map = 'map',
  Satellite = 'satellite',
}

export interface BasemapDefinition {
  id: BasemapId;

  /** Translation key for the layer switcher label. */
  labelKey: string;

  /** Leaflet URL template. Placeholders: {s} {z} {x} {y}. */
  urlTemplate: string;

  attribution: string;

  minZoom: number;

  /**
   * Deepest zoom the provider actually serves imagery for. Leaflet upscales
   * beyond this instead of requesting tiles that do not exist, so the map can
   * still zoom past it without going blank.
   */
  maxNativeZoom: number;

  /** How far the user may zoom in, upscaled past maxNativeZoom. */
  maxZoom: number;

  /**
   * Retina detection requests tiles one zoom level deeper, which doubles tile
   * volume and pushes straight past a provider's imagery ceiling. Only worth
   * it on providers with deep coverage and no request budget.
   */
  detectRetina: boolean;

  /**
   * Load tiles through the authenticated fetch + IndexedDB cache instead of a
   * plain <img>. Required for same-origin proxied layers, which need the JWT.
   */
  cached: boolean;
}

export interface BasemapAttachOptions {
  /**
   * Decides which basemaps this map may offer. Satellite is limited to the
   * powerlines customer; everyone else gets the street map only, exactly as
   * before the basemap switcher existed.
   *
   * Passed in rather than resolved inside the service because the customer is
   * fetched asynchronously — callers already hold it by the time they build
   * their map, so this keeps the decision race-free.
   */
  customerId?: number;

  /** Overrides the registry default — call sites have historically differed. */
  minZoom?: number;

  /** Overrides the registry default. */
  maxZoom?: number;

  /** Where the layer switcher goes. Defaults to topright, under the zoom control. */
  controlPosition?: string;

  /** Show the layer switcher. Defaults to on wherever more than one basemap exists. */
  showSwitcher?: boolean;
}
